'use client';
/** @jsxImportSource @emotion/react */

import React, {
  forwardRef,
  type ReactNode,
  useImperativeHandle,
  useRef,
  useMemo,
} from 'react';

import { type CountryCode } from 'libphonenumber-js/max';

import type { TextFieldProps } from '../text-field/TextField';

import { CoreIcon } from '../core-icon/CoreIcon';
import { IconButton } from '../icon-button/IconButton';
import { InputAdornment } from '../input-adornment/InputAdornment';
import { TextField } from '../text-field/TextField';
import { useWidth } from '../utils/useWidth';
import { Dropdown } from './components/dropdown/Dropdown';
import { usePhoneInput } from './hooks/usePhoneInput';
import { getDialCode, sanitizeDialCode, type CountryOption } from './utils';

export type PhoneInputProps = Omit<
  TextFieldProps,
  'defaultValue' | 'inputRef' | 'label' | 'onChange' | 'value'
> & {
  /**
   *  List of countries to be displayed in the dropdown
   */
  countries: CountryOption[];
  /**
   * Label for the phone number text field
   */
  label: string;
  /**
   * Aria-label for menu button
   */
  menuButtonLabel: string;
  /**
   * Aria-label for close button in mobile view
   */
  menuCloseButtonLabel: string;
  /**
   *  Label for the search input
   */
  menuSearchLabel: string;
  /**
   *  Title for the country code dropdown
   */
  menuTitle: string;
  /**
   *  render function for the country item in the list
   */
  renderMenuItem: (option: CountryOption) => ReactNode;
  /**
   *  The flag of selected country to be displayed in the menu button
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  selectedCountryFlag: (code: CountryCode | (string & {})) => ReactNode;
  /**
   *  Default country to be selected. If not provided, the first country in the list will be selected
   */
  defaultCountry?: Pick<CountryOption, 'code'>['code'];
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: string;
  /**
   * Input Ref for TextField
   */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Menu button ref
   */
  menuButtonRef?: React.Ref<HTMLButtonElement>;
  /**
   * Empty result message to display when there are no items
   */
  menuEmptyResultElement?: ReactNode;
  /**
   * The value of the phone number. Use when the component is controlled.
   */
  value?: string;
  /**
   *  onFilter event handler for menu search input, called for each change event
   */
  onFilter?: (country: CountryOption, query: string) => boolean;
  /**
   *  onInputChange event handler for phone number text field
   */
  onInputChange?: (formattedNumber: string) => void;
  /**
   *  onSelect event handler for the selected country in menu, called when a country is selected
   */
  onSelectCountry?: (country: CountryOption) => void;
};

const defaultFilter = (country: CountryOption, query: string): boolean => {
  // Special cases for empty query - always show all countries
  if (!query.trim()) {
    return true;
  }

  const lowerQuery = query.toLowerCase();

  // Check if we can match country name
  const nameMatch = country.name?.toLowerCase().includes(lowerQuery) || false;

  // Check if we can match dial code (either provided or derived)
  const effectiveDialCode = getDialCode(country); // Get the effective dial code
  const sanitizedEffectiveDialCode = sanitizeDialCode(effectiveDialCode); // Sanitize it (e.g. +1-684 -> +1 684)

  // Match against the sanitized effective dial code.
  // Users might type parts of the number, or the prefix.
  const dialCodeMatch = sanitizedEffectiveDialCode
    ? sanitizedEffectiveDialCode.toLowerCase().includes(lowerQuery)
    : false;

  // Check if we can match country code directly
  const codeMatch = country.code.toLowerCase().includes(lowerQuery);

  return nameMatch || dialCodeMatch || codeMatch;
};

export const PhoneInput = forwardRef<HTMLDivElement, PhoneInputProps>(
  (props, forwardedRef) => {
    const {
      name = 'phone',
      countries: rawCountries,
      defaultCountry: rawDefaultCountry,
      defaultValue,
      error,
      helperText,
      inputRef,
      label,
      menuButtonLabel,
      menuButtonRef,
      menuCloseButtonLabel,
      menuEmptyResultElement,
      menuSearchLabel,
      menuTitle,
      onFilter = defaultFilter,
      onInputChange,
      onSelectCountry,
      renderMenuItem,
      selectedCountryFlag,
      size,
      value,
      onBlur,
      ...rest
    } = props;

    const countries = useMemo(
      () =>
        rawCountries.map((country) => ({
          ...country,
          code: country.code.toUpperCase(),
        })),
      [rawCountries]
    );

    const defaultCountry = rawDefaultCountry?.toUpperCase();

    const localTextFieldRef = useRef<HTMLDivElement>(null);
    const {
      anchorEl,
      formattedPhoneNumber,
      onClose,
      onCountrySelect,
      onOpen,
      onPhoneNumberChange,
      handleBlur,
      selectedCountry,
    } = usePhoneInput({
      anchorEl: localTextFieldRef,
      countries,
      defaultCountry,
      defaultValue,
      onInputChange,
      value,
    });
    const localInputRef = useRef<HTMLInputElement>(null);
    const localButtonRef = useRef<HTMLButtonElement>(null);
    const width = useWidth(anchorEl);
    // Expose the refs to the outside when forwardRef is used
    useImperativeHandle(
      forwardedRef,
      () => localTextFieldRef.current as HTMLDivElement
    );
    useImperativeHandle(
      inputRef,
      () => localInputRef.current as HTMLInputElement
    );
    useImperativeHandle(
      menuButtonRef,
      () => localButtonRef.current as HTMLButtonElement
    );

    const onMenuItemClick = (item: CountryOption) => {
      onCountrySelect(item);
      onSelectCountry?.(item);
      /**
       * The `setTimeout` with a delay of 0 is used to ensure that the input field
       * is focused after the dialog has fully closed and its transition has finished.
       * This prevents focus issues that might occur if we attempt to focus the input
       * while the dialog is still in the process of closing.
       */
      setTimeout(() => {
        localInputRef.current?.focus();
      }, 0);
    };

    const hasSingleCountry = countries.length === 1;

    // Combine internal handleBlur with custom onBlur handler
    const handleCombinedBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      handleBlur(e);
      onBlur?.(e);
    };

    return (
      <>
        <TextField
          {...rest}
          name={name}
          ref={localTextFieldRef}
          InputProps={{
            startAdornment:
              countries.length > 0 ? (
                <InputAdornment position="start">
                  <IconButton
                    ref={localButtonRef}
                    sx={(theme) => ({
                      borderRadius: theme.ui.focus.borderRadius,
                      ...(size === 'small'
                        ? {
                            p: '2px 4px 2px 4px',
                          }
                        : {
                            pr: '4px',
                          }),
                      ...(rest.disabled && {
                        '& > *:first-of-type': {
                          opacity: theme.palette.action.disabledOpacity,
                        },
                      }),
                      ...(Boolean(anchorEl) && {
                        backgroundColor: theme.palette.action.selected,
                      }),
                    })}
                    aria-label={menuButtonLabel}
                    disabled={rest.disabled || hasSingleCountry}
                    size={size}
                    onClick={anchorEl ? onClose : onOpen}
                  >
                    {selectedCountryFlag(selectedCountry.code)}
                    {!hasSingleCountry && (
                      <CoreIcon
                        name="chevrondown"
                        size="sm"
                        sx={{
                          ml: '4px',
                          ...(Boolean(anchorEl) && {
                            transform: 'rotate(180deg)',
                          }),
                        }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          }}
          autoComplete="off"
          error={error}
          helperText={helperText}
          inputRef={localInputRef}
          label={label}
          size={size}
          type="tel"
          value={formattedPhoneNumber}
          onChange={onPhoneNumberChange}
          onBlur={handleCombinedBlur}
        />

        {countries.length > 0 && !hasSingleCountry && (
          <Dropdown
            MenuListProps={{
              /**
               * The reason why we are using `div` here is because the MenuList component cannot be `ul` since we have virtualized list inside it,
               * and it can only be `div`. We are using virtualized list in other place as well.
               */
              component: 'div',
            }}
            slotProps={{
              paper: {
                style: {
                  /**
                   * Cannot use minWidth here because it will not take its width into account (position absolute) and will not be able to calculate the width of the dropdown
                   * so we need to use width to set the width of the dropdown and maxWidth to set the maximum width of the dropdown
                   */
                  width,
                },
                sx: (theme) => ({
                  borderRadius: `${theme.shape.borderRadiusMd}px`,
                }),
              },
            }}
            anchorEl={anchorEl}
            items={countries}
            menuButtonRef={localButtonRef}
            menuCloseButtonLabel={menuCloseButtonLabel}
            menuEmptyResultElement={menuEmptyResultElement}
            menuSearchLabel={menuSearchLabel}
            menuTitle={menuTitle}
            onClose={onClose}
            onFilter={onFilter}
            onMenuItemClick={onMenuItemClick}
          >
            <Dropdown.VirtualizedList>
              {renderMenuItem}
            </Dropdown.VirtualizedList>
          </Dropdown>
        )}
      </>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export { type CountryOption };

// Re-export libphonenumber-js functions
export {
  parsePhoneNumber,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js/max';
